import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ISortState } from './table-representantes-legal.interface';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ToastService } from 'src/app/modules/services/toast/toast.service';
import { ModalController } from '@ionic/angular';
import { MatDialog } from '@angular/material/dialog';
import { ModalRepresentanteLegalComponent } from '../modal-representante-legal/modal-representante-legal.component';
import { ModalComponent } from '../../modal/modal.component';
import { ModalHistoricComponent } from 'src/app/modules/shared/modals/modal-historic/modal-historic.component';

@Component({
  selector: 'app-table-representante-legal',
  templateUrl: './table-representante-legal.component.html',
  styleUrls: ['./table-representante-legal.component.scss'],
})
export class TableRepresentanteLegalComponent {
  @Input() allData: any[] = [];
  @Input() isMaster: boolean = false;
  @Output() allDataChange = new EventEmitter<any>();
  @Input() inclusaoDisabled: boolean = false;
  @Input() exclusaoDisabled: boolean = false;
  @Input() edicaoDisabled: boolean = false;
  disabledTooltipMessage: string = 'Ação não permitida';

  sortState: ISortState = {
    sortProperty: 'nomeCompleto',
    sortDirection: 'asc',
  };
  pageSize: number = 10;
  pageIndex: number = 0;
  totalPages: number = 1;
  totalElements: number = 0;
  displayedColumns: string[] = [
    'nomeCompleto',
    'cpf',
    'funcao',
    'telefone',
    'email',
    'actions',
  ];

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  constructor(
    private toastService: ToastService,
    private modalCtrl: ModalController,
    public dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['allData']) {
      this.allData = this.allData;
      this.dataSource.data = this.allData;
    }
  }

  async openNewRepresentanteDialog(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ModalRepresentanteLegalComponent,
      backdropDismiss: false,
      cssClass: 'modal-lg',
      componentProps: { isEditMode: false },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.allData.push(data);
      this.dataSource.data = this.allData;
      this.allDataChange.emit(this.allData);
    }
  }

  async openEditRepresentanteDialog(representante: any): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ModalRepresentanteLegalComponent,
      backdropDismiss: false,
      cssClass: 'modal-lg',
      componentProps: { data: representante, isEditMode: true },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.onUpdate(data);
    }
  }

  openDeleteDialog(id: string): void {
    const modalTitle: string = 'Excluir';
    const modalContent: string =
      'Você tem certeza que deseja excluir este representante?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onDelete.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  openInactivateDialog(id: any): void {
    const modalTitle: string = 'Inativar';
    const modalContent: string =
      'Você tem certeza que deseja inativar este representante?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onInactivateModalConfirm.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  openActivateDialog(id: any): void {
    const modalTitle: string = 'Ativar';
    const modalContent: string =
      'Você tem certeza que deseja ativar este representante?';

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: this.onActivateModalConfirm.bind(this),
        modalTitle: modalTitle,
        modalContent: modalContent,
        id: id,
      },
    });
  }

  onInactivateModalConfirm(id: string): void {
    let representanteObj = this.allData.find((data) =>
      data.id ? data.id === id : data.tempId === id
    );
    representanteObj.status = false;

    this.onUpdate(representanteObj);
    this.toastService.showSuccess('Representante ativado com sucesso');
  }

  onActivateModalConfirm(id: string): void {
    let representanteObj = this.allData.find((data) =>
      data.id ? data.id === id : data.tempId === id
    );
    representanteObj.status = true;

    this.onUpdate(representanteObj);
    this.toastService.showSuccess('Representante ativado com sucesso');
  }

  onDelete(id: string) {
    let newDataArray = this.allData.filter((oldData) =>
      oldData.id ? oldData.id !== id : oldData.tempId !== id
    );
    this.allData = newDataArray;
    this.dataSource.data = newDataArray;
    this.toastService.showSuccess('Representante removido com sucesso');
    this.allDataChange.emit(this.allData);
  }

  onUpdate(data: any) {
    let newDataArray = this.allData.filter((oldData) =>
      oldData.id ? oldData.id !== data.id : oldData.tempId !== data.tempId
    );
    newDataArray.push(data);
    this.allData = newDataArray;
    this.dataSource.data = newDataArray;
    this.allDataChange.emit(this.allData);
  }

  onSortChange(sortProperty: ISortState['sortProperty']) {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection =
        this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }

    const sortedData = this.dataSource.data.sort((a, b) => {
      let comparison = 0;
      if (a[sortProperty] > b[sortProperty]) {
        comparison = 1;
      } else if (a[sortProperty] < b[sortProperty]) {
        comparison = -1;
      }

      return this.sortState.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.dataSource.data = sortedData;
  }

  onSortChangeKeydown(
    event: KeyboardEvent,
    sortProperty: ISortState['sortProperty']
  ) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      this.onSortChange(sortProperty);
    }
  }
  async openHistoricModal(id: string) {
    
    const data = {
      id: id,
      type: 'representante',
      fieldMappings: {
        criadoEm: 'Criado em',
        nomeCompleto: 'Nome Completo',
        funcao: 'Função',
        email: 'E-mail',
        telefone: 'Telefone',
        cpf: 'CPF',
      },
    };

    const modal = await this.modalCtrl.create({
      component: ModalHistoricComponent,
      componentProps: {
        data: data,
        fieldMappings: data.fieldMappings,
      },
    });

    modal.present();
  }
}
